import LockPersonIcon from '@mui/icons-material/LockPerson';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	InputAdornment,
	TextField,
	Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import strings from '../../Globalization';
import { authUnset } from '../../actions';
import { useSetState } from '../../hooks';
import connectionService from '../../services/Connection';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
	'& .MuiDialogContent-root': {
		padding: theme.spacing(2),
		minWidth: '428px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 5
	},
	'& .MuiDialogActions-root': {
		padding: theme.spacing(1),
		alignItems: 'center',
		justifyContent: 'center'
	}
}));

const SessionTimeoutDialog = ({ open, onClose }) => {
	const { tags } = useSelector((state) => state.auth);
	const username = tags?.Username;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const passwordRef = useRef();
	const [state, setState] = useSetState({
		open: false,
		showPassword: false,
		loading: false
	});
	const handleToggleShowPassword = () => setState(({ showPassword }) => ({ showPassword: !showPassword }));

	const handlePasswordChange = useCallback((e) => {
		passwordRef.current = e.target.value;
		if (state.error) {
			setState({ error: undefined })
		}
	}, [state.error]);

	const continueSession = (username, password) => {
		setState({ loading: true });
		const formData = new FormData();
		formData.set('username', username);
		formData.set('password', password);
		formData.set('clientId', tags.ClientId);
		formData.set('rememberMe', true);
		formData.set('timeZoneOffSet', new Date().getTimezoneOffset());
		fetch(connectionService.Login, { method: 'POST', body: formData, credentials: 'include' })
			.then((res) => res.json())
			.then(({ message, success }) => {
				if (success) {
					window.location.reload();
				} else {
					if (message === 'AccountLocked') {
						Swal.fire({
							titleText: strings.Alert,
							text: strings.AccountLocked,
							closeOnConfirm: true,
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						}).then(() => {
							navigate('/login');
							dispatch(authUnset());
						});
					} else {
						Swal.fire({
							titleText: strings.Alert,
							text: strings.ValidPasswordMessage,
							closeOnConfirm: true,
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						}).then(() => {

						});
						//window.queuedScope._onSessionExpire('', '', window.queuedScope, strings.ValidPasswordMessage);
					}
				}
			})
			.catch(() => { })
			.finally(() => setState({ loading: false }));
	};

	const handleSubmit = useCallback(
		(e) => {
			e?.preventDefault?.();
			if (!passwordRef.current?.trim?.()) {
				setState({ error: strings.PasswordMissing });
				return;
			}
			continueSession(username, passwordRef.current);
		},
		[username]
	);

	return (
		<BootstrapDialog
			component="form"
			autoComplete="off"
			maxWidth={'lg'}
			onSubmit={handleSubmit}
			onClose={onClose}
			aria-labelledby="session-timeout-dialog-title"
			open={open}
			sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
		>
			<DialogTitle sx={{ m: 0, p: 2 }} id="session-timeout-dialog-title">
				{strings.SessionTitle}
			</DialogTitle>
			<DialogContent dividers>
				<Typography variant="h2">
					<LockPersonIcon color="disabled" fontSize="inherit" />
				</Typography>

				<DialogContentText sx={{ m: 2 }}>{strings.SessionMessage}</DialogContentText>
				{state.loading && <CircularProgress size={24} />}
				<TextField
					label={strings.Password}
					placeholder={strings.Password}
					sx={{ m: 1, minWidth: 320 }}
					autoFocus
					size="small"
					margin="dense"
					defaultValue={passwordRef.current}
					onChange={handlePasswordChange}
					disabled={state.loading}
					type={state.showPassword ? 'text' : 'password'}
					error={!!state.error}
					helperText={state.error}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSubmit(e);
						}
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleToggleShowPassword}
									onMouseDown={handleToggleShowPassword}
									edge="end"
								>
									{state.showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						)
					}}
					inputProps={{
						autocomplete: 'new-password',
						form: {
							autocomplete: 'off'
						}
					}}
					variant="outlined"
				/>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" color="success" type={'submit'} disableElevation>
					{strings.Ok}
				</Button>
				<Button
					aria-label="close"
					onClick={onClose}
					variant="contained"
					color="error"
					type={'button'}
					disableElevation
				>
					{strings.Cancel}
				</Button>
			</DialogActions>
		</BootstrapDialog>
	);
};

export default memo(SessionTimeoutDialog);
